import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';

const UserManagement = () => {
  const { user } = useAuth();
  // `user` est masqué par le paramètre du map plus bas : on capture l'identifiant
  // ici pour pouvoir masquer l'action de désactivation sur son propre compte.
  const currentUserId = user?.id || user?._id;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // Le mot de passe ne se modifie que sur geste explicite : sinon on le laisse
  // masqué, pour ne pas laisser croire qu'un champ vide le réinitialise.
  const [editingPassword, setEditingPassword] = useState(false);
  // La suppression est définitive : on exige que le nom soit retapé à l'identique.
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'marketing',
    department: 'marketing',
    zoneId: '',
    internalSiteId: '',
    distributorId: '',
    isSignatory: false,
  });
  const [zones, setZones] = useState([]);
  const [internalSites, setInternalSites] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [motDePasseVisible, setMotDePasseVisible] = useState(false);
  const [departementActif, setDepartementActif] = useState('tous');
  const [sousRoleFDV, setSousRoleFDV] = useState('tous'); // sous-filtre de l'onglet Force de vente
  const [zoneFDV, setZoneFDV] = useState(''); // filtre par zone de l'onglet Force de vente

  // Génère un mot de passe fort et lisible : 14 caractères tirés de crypto,
  // au moins une minuscule, une majuscule, un chiffre et un symbole, sans
  // caractères ambigus (0/O, 1/l/I) pour éviter les erreurs de recopie.
  const genererMotDePasse = () => {
    const jeux = ['abcdefghijkmnpqrstuvwxyz', 'ABCDEFGHJKLMNPQRSTUVWXYZ', '23456789', '!@#$%*-_?'];
    const tous = jeux.join('');
    const rnd = (n) => {
      const a = new Uint32Array(1);
      window.crypto.getRandomValues(a);
      return a[0] % n;
    };
    const chars = jeux.map((j) => j[rnd(j.length)]); // un de chaque catégorie
    while (chars.length < 14) chars.push(tous[rnd(tous.length)]);
    for (let i = chars.length - 1; i > 0; i -= 1) {
      const j = rnd(i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    const mdp = chars.join('');
    setFormData((prev) => ({ ...prev, password: mdp }));
    setMotDePasseVisible(true);
    return mdp;
  };

  useEffect(() => {
    // Only directrice_marketing should access this page
    if (user && user.role !== 'directrice_marketing') {
      window.location.href = '/dashboard';
    } else {
      fetchUsers();
      fetchAffiliations();
    }
  }, [user]);

  // Zones et sites industriels alimentent les listes de rattachement : un
  // animateur doit être dans une zone, un business unit sur un site.
  const fetchAffiliations = async () => {
    try {
      const [z, s, d] = await Promise.all([
        api.get('/api/zones'),
        api.get('/api/internal-sites'),
        api.distributors.getAll(false, '', 1, 1000),
      ]);
      setZones(z.data.data || []);
      setInternalSites(s.data.data || []);
      setDistributors(d.data.data || []);
    } catch (error) {
      console.error('Error fetching zones/sites:', error);
      toast.error('Erreur lors du chargement des zones et sites industriels');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      setUsers(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      
      await api.post('/api/users', formData);
      
      toast.success('Utilisateur ajouté avec succès');
      setShowAddModal(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'marketing',
        department: 'marketing',
        zoneId: '',
        internalSiteId: '',
        distributorId: '',
        isSignatory: false,
      });
      fetchUsers();
      setFormLoading(false);
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout de l\'utilisateur');
      setFormLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    try {
      setFormLoading(true);
      
      const { password, ...profil } = formData;

      if (editingPassword && (!password || password.length < 8)) {
        toast.error('Le mot de passe doit contenir au moins 8 caractères');
        setFormLoading(false);
        return;
      }

      // Le profil part en premier : c'est l'appel qui peut encore échouer côté
      // serveur (email déjà pris). La réinitialisation, elle, coupe les
      // sessions et envoie un email — on ne la déclenche qu'une fois le reste
      // passé, pour ne pas laisser d'effet de bord derrière une erreur.
      await api.put(`/api/users/${selectedUser._id}`, profil);

      // Le mot de passe a sa propre route : il doit passer par le hook de
      // hachage du modèle, que findByIdAndUpdate ne déclenche pas.
      if (editingPassword) {
        await api.put(`/api/users/${selectedUser._id}/password`, { password });
      }

      toast.success(
        editingPassword
          ? 'Utilisateur mis à jour, mot de passe réinitialisé'
          : 'Utilisateur mis à jour avec succès'
      );
      setShowEditModal(false);
      setEditingPassword(false);
      fetchUsers();
      setFormLoading(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;

    const activer = selectedUser.isActive === false;

    try {
      setFormLoading(true);

      await api.put(`/api/users/${selectedUser._id}/status`, { isActive: activer });

      toast.success(activer ? 'Compte activé' : 'Compte désactivé');
      setShowStatusModal(false);
      fetchUsers();
      setFormLoading(false);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.response?.data?.error || 'Erreur lors du changement de statut');
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (deleteConfirmation !== selectedUser.name) return;

    try {
      setFormLoading(true);

      await api.delete(`/api/users/${selectedUser._id}`);

      toast.success('Utilisateur supprimé avec succès');
      setShowDeleteModal(false);
      fetchUsers();
      setFormLoading(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression de l\'utilisateur');
      setFormLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditingPassword(false);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      department: user.department,
      zoneId: user.zoneId || '',
      internalSiteId: user.internalSiteId?._id || user.internalSiteId || '',
      distributorId: user.distributorId?._id || user.distributorId || '',
      isSignatory: user.isSignatory === true,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteConfirmation('');
    setShowDeleteModal(true);
  };

  const openStatusModal = (user) => {
    setSelectedUser(user);
    setShowStatusModal(true);
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'directrice_marketing': return 'Directrice Marketing';
      case 'marketing': return 'Marketing';
      case 'force_de_vente': return 'Force de Vente';
      case 'commercial': return 'Commercial';
      case 'chef_de_zone': return 'Chef de Zone';
      case 'animateur': return 'Animateur';
      case 'business_unit': return 'Business Unit';
      case 'distributeur': return 'Distributeur';
      default: return role;
    }
  };

  // Un animateur doit être dans une zone, un business unit sur un site
  // industriel. Le champ n'apparaît que pour le rôle concerné, et il est requis.
  const renderAffiliation = (prefix) => {
    if (formData.role === 'animateur') {
      return (
        <div>
          <label htmlFor={`${prefix}-zoneId`} className="block text-sm font-medium text-gray-700">
            Zone <span className="text-red-500">*</span>
          </label>
          <select
            name="zoneId"
            id={`${prefix}-zoneId`}
            value={formData.zoneId}
            onChange={handleChange}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Sélectionner une zone</option>
            {zones.map((z) => (
              <option key={z._id} value={z._id}>
                {z.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Un animateur travaille toujours dans une zone. La synchronisation du répertoire FDV
            fait autorité et pourra corriger ce rattachement.
          </p>
        </div>
      );
    }

    if (formData.role === 'distributeur') {
      return (
        <div>
          <label htmlFor={`${prefix}-distributorId`} className="block text-sm font-medium text-gray-700">
            Distributeur <span className="text-red-500">*</span>
          </label>
          <select
            name="distributorId"
            id={`${prefix}-distributorId`}
            value={formData.distributorId}
            onChange={handleChange}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Sélectionner un distributeur</option>
            {distributors.map((d) => (
              <option key={d._id} value={d._id}>
                {d.code} — {d.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Ce compte signera les décharges des demandes adressées à ce distributeur.
          </p>
        </div>
      );
    }

    // Seuls les marketeurs signataires peuvent valider l'étape marketing.
    if (formData.role === 'marketing') {
      return (
        <div className="flex items-start">
          <input
            type="checkbox"
            name="isSignatory"
            id={`${prefix}-isSignatory`}
            checked={formData.isSignatory}
            onChange={handleChange}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor={`${prefix}-isSignatory`} className="ml-2 block text-sm text-gray-700">
            Signataire
            <span className="block text-xs text-gray-500">
              Seuls les signataires valident l'étape marketing. Les autres peuvent créer des demandes,
              qui attendent ensuite un signataire.
            </span>
          </label>
        </div>
      );
    }

    if (formData.role === 'business_unit') {
      return (
        <div>
          <label htmlFor={`${prefix}-internalSiteId`} className="block text-sm font-medium text-gray-700">
            Site industriel <span className="text-red-500">*</span>
          </label>
          <select
            name="internalSiteId"
            id={`${prefix}-internalSiteId`}
            value={formData.internalSiteId}
            onChange={handleChange}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Sélectionner un site</option>
            {internalSites.map((s) => (
              <option key={s._id} value={s._id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return null;
  };

  const getDepartmentName = (department) => {
    switch (department) {
      case 'marketing': return 'Marketing';
      case 'force_de_vente': return 'Force de Vente';
      case 'commercial': return 'Commercial';
      case 'direction': return 'Direction';
      case 'distributeur': return 'Distributeur';
      case 'business_unit': return 'Business Unit';
      default: return department;
    }
  };

  // Onglets par département : ordre fixe pour ceux qu'on connaît, puis tout
  // département supplémentaire trouvé en base, et enfin « Sans département ».
  const ordreDepts = ['marketing', 'force_de_vente', 'commercial', 'direction', 'business_unit', 'distributeur'];
  const deptDe = (u) => u.department || 'sans';
  const deptsPresents = [...new Set(users.map(deptDe))].sort(
    (a, b) => (ordreDepts.indexOf(a) + 1 || 99) - (ordreDepts.indexOf(b) + 1 || 99)
  );
  const onglets = [
    { cle: 'tous', libelle: 'Tous', n: users.length },
    ...deptsPresents.map((d) => ({
      cle: d,
      libelle: d === 'sans' ? 'Sans département' : getDepartmentName(d),
      n: users.filter((u) => deptDe(u) === d).length,
    })),
  ];
  let usersAffiches = departementActif === 'tous'
    ? users
    : users.filter((u) => deptDe(u) === departementActif);

  // Sous-filtre par rôle dans le département Force de vente (force de vente,
  // chef de zone, animateur), qui regroupe plusieurs rôles.
  const sousRolesFDV = [
    { cle: 'tous', libelle: 'Tous' },
    { cle: 'force_de_vente', libelle: 'Force de vente' },
    { cle: 'chef_de_zone', libelle: 'Chef de zone' },
    { cle: 'animateur', libelle: 'Animateur' },
  ];
  if (departementActif === 'force_de_vente' && sousRoleFDV !== 'tous') {
    usersAffiches = usersAffiches.filter((u) => u.role === sousRoleFDV);
  }
  if (departementActif === 'force_de_vente' && zoneFDV) {
    usersAffiches = usersAffiches.filter((u) => u.zoneId === zoneFDV);
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des utilisateurs</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Ajouter un utilisateur
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px px-2" aria-label="Départements">
            {onglets.map((o) => (
              <button
                key={o.cle}
                type="button"
                onClick={() => { setDepartementActif(o.cle); setSousRoleFDV('tous'); setZoneFDV(''); }}
                className={`whitespace-nowrap py-3 px-4 border-b-2 text-sm font-medium ${
                  departementActif === o.cle
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {o.libelle}
                <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{o.n}</span>
              </button>
            ))}
          </nav>
        </div>
        {departementActif === 'force_de_vente' && (
          <div className="flex gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 overflow-x-auto">
            {sousRolesFDV.map((s) => (
              <button
                key={s.cle}
                type="button"
                onClick={() => setSousRoleFDV(s.cle)}
                className={`whitespace-nowrap px-3 py-1 text-xs font-medium rounded-full border ${
                  sousRoleFDV === s.cle
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {s.libelle}
              </button>
            ))}
            <select
              value={zoneFDV}
              onChange={(e) => setZoneFDV(e.target.value)}
              aria-label="Filtrer par zone"
              className={`px-3 py-1 text-xs font-medium rounded-full border ${
                zoneFDV
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
              }`}
            >
              <option value="">Toutes les zones</option>
              {zones.map((z) => (
                <option key={z._id} value={z._id}>Zone {z.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Département</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signature</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersAffiches.length > 0 ? (
                usersAffiches.map((user) => (
                  <tr key={user._id} className={`hover:bg-gray-50 ${user.isActive === false ? 'bg-gray-100' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getRoleName(user.role)}
                      {['animateur', 'chef_de_zone'].includes(user.role) && (
                        <span className="block text-xs text-gray-400">
                          {user.zoneName ? `Zone ${user.zoneName}` : (
                            <span className="text-amber-600">Aucune zone</span>
                          )}
                        </span>
                      )}
                      {user.role === 'marketing' && user.isSignatory && (
                        <span className="block text-xs text-green-600">Signataire</span>
                      )}
                      {user.role === 'distributeur' && (
                        <span className="block text-xs text-gray-400">
                          {user.distributorId?.code ? `Dépôt ${user.distributorId.code}` : (
                            <span className="text-amber-600">Aucun distributeur</span>
                          )}
                        </span>
                      )}
                      {user.role === 'business_unit' && (
                        <span className="block text-xs text-gray-400">
                          {user.internalSiteId?.code ? `Site ${user.internalSiteId.code}` : (
                            <span className="text-amber-600">Aucun site</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getDepartmentName(user.department)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.signatureUrl ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Non
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.isActive === false ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Modifier
                      </button>
                      {String(user._id) !== String(currentUserId) && (
                        <button
                          onClick={() => openStatusModal(user)}
                          className={`mr-4 ${
                            user.isActive === false
                              ? 'text-green-600 hover:text-green-900'
                              : 'text-amber-600 hover:text-amber-900'
                          }`}
                        >
                          {user.isActive === false ? 'Activer' : 'Désactiver'}
                        </button>
                      )}
                      <button
                        onClick={() => openDeleteModal(user)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Add User Modal */}
      {showAddModal && (
        <Modal
          open
          onClose={() => setShowAddModal(false)}
          title="Ajouter un utilisateur"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="user-add-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <form id="user-add-form" onSubmit={handleAddUser}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Mot de passe
                          </label>
                          <div className="mt-1 flex gap-2">
                            <input
                              type={motDePasseVisible ? 'text' : 'password'}
                              name="password"
                              id="password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                              minLength={8}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                            <button
                              type="button"
                              onClick={() => setMotDePasseVisible((v) => !v)}
                              className="px-2 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              {motDePasseVisible ? 'Masquer' : 'Afficher'}
                            </button>
                            <button
                              type="button"
                              onClick={genererMotDePasse}
                              className="px-2 text-xs whitespace-nowrap text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                              Générer
                            </button>
                            {formData.password && (
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard?.writeText(formData.password);
                                  toast.success('Mot de passe copié');
                                }}
                                className="px-2 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                              >
                                Copier
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                            Rôle
                          </label>
                          <select
                            name="role"
                            id="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="marketing">Marketing</option>
                            <option value="force_de_vente">Force de Vente</option>
                            <option value="commercial">Commercial</option>
                            <option value="directrice_marketing">Directrice Marketing</option>
                            <option value="chef_de_zone">Chef de Zone</option>
                            <option value="animateur">Animateur</option>
                            <option value="business_unit">Business Unit</option>
                            <option value="distributeur">Distributeur</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                            Département
                          </label>
                          <select
                            name="department"
                            id="department"
                            value={formData.department}
                            onChange={handleChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="marketing">Marketing</option>
                            <option value="force_de_vente">Force de Vente</option>
                            <option value="commercial">Commercial</option>
                            <option value="direction">Direction</option>
                            <option value="business_unit">Business Unit</option>
                            <option value="distributeur">Distributeur</option>
                          </select>
                        </div>
            {renderAffiliation('add')}
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <Modal
          open
          onClose={() => setShowEditModal(false)}
          title="Modifier l'utilisateur"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="user-edit-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </>
          }
        >
          <form id="user-edit-form" onSubmit={handleEditUser}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="edit-email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700">
                              Mot de passe
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingPassword(!editingPassword);
                                setFormData((prev) => ({ ...prev, password: '' }));
                              }}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              {editingPassword ? 'Annuler' : 'Éditer'}
                            </button>
                          </div>
                          {editingPassword ? (
                            <>
                              <div className="mt-1 flex gap-2">
                                <input
                                  type={motDePasseVisible ? 'text' : 'password'}
                                  name="password"
                                  id="edit-password"
                                  value={formData.password}
                                  onChange={handleChange}
                                  minLength={8}
                                  autoComplete="new-password"
                                  placeholder="8 caractères minimum"
                                  className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                                <button
                                  type="button"
                                  onClick={() => setMotDePasseVisible((v) => !v)}
                                  className="px-2 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                  title={motDePasseVisible ? 'Masquer' : 'Afficher'}
                                >
                                  {motDePasseVisible ? 'Masquer' : 'Afficher'}
                                </button>
                                <button
                                  type="button"
                                  onClick={genererMotDePasse}
                                  className="px-2 text-xs whitespace-nowrap text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                  Générer
                                </button>
                                {formData.password && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(formData.password);
                                      toast.success('Mot de passe copié');
                                    }}
                                    className="px-2 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                  >
                                    Copier
                                  </button>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                Le nouveau mot de passe ferme les sessions ouvertes de cet utilisateur et lui
                                est signalé par email. À lui transmettre vous-même.
                              </p>
                              {formData.password && formData.password.length < 8 && (
                                <p className="mt-1 text-xs text-red-600">
                                  Trop court : {formData.password.length} caractère
                                  {formData.password.length > 1 ? 's' : ''} sur 8 minimum.
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="mt-1 text-sm text-gray-400">
                              ••••••••&nbsp;&nbsp;<span className="text-xs">inchangé</span>
                            </p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">
                            Rôle
                          </label>
                          <select
                            name="role"
                            id="edit-role"
                            value={formData.role}
                            onChange={handleChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="marketing">Marketing</option>
                            <option value="force_de_vente">Force de Vente</option>
                            <option value="commercial">Commercial</option>
                            <option value="directrice_marketing">Directrice Marketing</option>
                            <option value="chef_de_zone">Chef de Zone</option>
                            <option value="animateur">Animateur</option>
                            <option value="business_unit">Business Unit</option>
                            <option value="distributeur">Distributeur</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="edit-department" className="block text-sm font-medium text-gray-700">
                            Département
                          </label>
                          <select
                            name="department"
                            id="edit-department"
                            value={formData.department}
                            onChange={handleChange}
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="marketing">Marketing</option>
                            <option value="force_de_vente">Force de Vente</option>
                            <option value="commercial">Commercial</option>
                            <option value="direction">Direction</option>
                            <option value="business_unit">Business Unit</option>
                            <option value="distributeur">Distributeur</option>
                          </select>
                        </div>
            {renderAffiliation('edit')}
            </div>
          </form>
        </Modal>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <Modal
          open
          onClose={() => setShowDeleteModal(false)}
          title="Supprimer définitivement"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="modal-btn-danger"
                onClick={handleDeleteUser}
                disabled={formLoading || deleteConfirmation !== selectedUser.name}
              >
                {formLoading ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Le compte <span className="font-medium text-gray-900">{selectedUser.name}</span> sera
                effacé de la base, ainsi que sa signature et son cachet. Les documents qu'il a déjà
                signés resteront, mais ne renverront plus vers aucun compte.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Pour un départ ou une absence, préférez <span className="font-medium">Désactiver</span> :
                le compte perd l'accès immédiatement et reste réactivable.
              </p>
              <label className="block mt-4 text-sm text-gray-700">
                Retapez <span className="font-medium text-gray-900">{selectedUser.name}</span> pour confirmer :
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                placeholder={selectedUser.name}
                autoComplete="off"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Activation / désactivation */}
      {showStatusModal && selectedUser && (
        <Modal
          open
          onClose={() => setShowStatusModal(false)}
          title={selectedUser.isActive === false ? 'Activer le compte' : 'Désactiver le compte'}
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowStatusModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className={
                  selectedUser.isActive === false
                    ? 'modal-btn-success'
                    : 'modal-btn bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500'
                }
                onClick={handleToggleStatus}
                disabled={formLoading}
              >
                {formLoading
                  ? 'En cours...'
                  : selectedUser.isActive === false
                  ? 'Activer'
                  : 'Désactiver'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div
              className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                selectedUser.isActive === false ? 'bg-green-100' : 'bg-amber-100'
              }`}
            >
                    <svg
                      className={`h-6 w-6 ${selectedUser.isActive === false ? 'text-green-600' : 'text-amber-600'}`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {selectedUser.isActive === false ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      )}
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <div className="mt-2">
                      {selectedUser.isActive === false ? (
                        <p className="text-sm text-gray-500">
                          <span className="font-medium text-gray-900">{selectedUser.name}</span> pourra de
                          nouveau se connecter avec son mot de passe habituel.
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-500">
                            <span className="font-medium text-gray-900">{selectedUser.name}</span> perdra
                            l'accès immédiatement, y compris sur une session déjà ouverte. Ses appareils de
                            confiance seront oubliés.
                          </p>
                          <p className="mt-2 text-sm text-gray-500">
                            Le compte et ses documents sont conservés, et vous pourrez le réactiver à tout moment.
                          </p>
                        </>
                      )}
                      {selectedUser.role === 'animateur' && (
                        <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                          Ce compte est un animateur : une prochaine synchronisation du répertoire FDV
                          rétablira le statut prévu par le répertoire.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;
